import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { FormTutorial, HowToResponse, Tutorial } from '../models/tutorial';
import { Observable, Subject, tap } from 'rxjs';

interface HowToQueryParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  sortColumn?: 'createdAt' | 'name';
  sortOrder?: string;
  tag?: string;
}

@Injectable({
  providedIn: 'root'
})
export class HowToService {
  private readonly apiUrl = `${environment.apiUrl}/tutorials`;
  private refreshTutorials = new Subject<void>();

  constructor(private http: HttpClient) {}

  getTutorials(options?: HowToQueryParams) {
    const params = new HttpParams()
      .set('pageNumber', options?.pageNumber || 1)
      .set('pageSize', options?.pageSize || 100)
      .set('searchTerm', options?.searchTerm || '')
      .set('tag', options?.tag || '')
      .set('sortColumn', options?.sortColumn || 'createdAt')
      .set('sortOrder', options?.sortOrder || 'desc');
    return this.http.get<HowToResponse>(`${this.apiUrl}`, { params });
  }

  downloadPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/file`, { responseType: 'blob' }).pipe(
      tap(file => {
        const fileUrl = URL.createObjectURL(file);
        const windowRef = window.open(fileUrl, '_blank');
        windowRef?.addEventListener('beforeunload', () => URL.revokeObjectURL(fileUrl));
      })
    );
  }

  create(data: { form: FormTutorial; file: File | null; thumbnail: File | null }): Observable<unknown> {
    return this.http.post(`${this.apiUrl}`, this.createFormData(data));
  }

  delete({ id }: Tutorial) {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  update(id: number, data: { form: FormTutorial; file: File | null; thumbnail: File | null }) {
    return this.http.put(`${this.apiUrl}/${id}`, this.createFormData(data));
  }

  getTags() {
    return this.http.get<string[]>(`${this.apiUrl}/tags`);
  }

  refreshTutorialList() {
    this.refreshTutorials.next();
  }

  listRefreshed$() {
    return this.refreshTutorials.asObservable();
  }

  private createFormData(data: { form: FormTutorial; file: File | null; thumbnail: File | null }) {
    const formData = new FormData();
    Object.entries(data.form).forEach(([key, value]) => {
      if (key === 'tags') {
        (value as string[]).forEach(tag => formData.append(key, tag));
      } else {
        formData.append(key, value);
      }
    });
    if (data.form.type === 'Pdf') {
      formData.delete('url');
      if (data.file) {
        formData.append('file', data.file, data.file.name);
      }
    }
    if (data.thumbnail) {
      formData.append('thumbnail', data.thumbnail, data.thumbnail.name);
    }
    return formData;
  }
}
